import React from 'react';

import { faLock, faLockOpen, faRightLeft } from '@fortawesome/pro-regular-svg-icons';

import Dropdown from 'components/dropdown';
import { put } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import type { Parent } from 'bundles/crm/types';

const iconsByName: Record<string, IconDefinition> = {
  'lock': faLock,
  'lock-open': faLockOpen,
};

export interface ReparentingOption {
  icon?: IconDefinition;
  iconName?: string;
  label: string;
  parent: Parent;
}

export interface ReparentableRecord {
  parentId?: number;
  parentType?: string;
  path?: string;
}

export interface Props<RecordType extends ReparentableRecord> {
  parentOptions?: ReparentingOption[];
  record: RecordType;
  onReparent?: (record: RecordType) => void;
}

export default function ReparentingMenuItems<RecordType extends ReparentableRecord>({
  parentOptions,
  record,
  onReparent,
}: Props<RecordType>) {
  const options = parentOptions?.filter(
    option => option.parent.id !== record.parentId || option.parent.type !== record.parentType
  );

  if (!options?.length) {
    return null;
  }

  async function reparentRecord(parent: Parent) {
    try {
      const updatedRecord: RecordType = await put(record.path, {
        parentId: parent.id,
        parentType: parent.type,
      });

      if (onReparent) {
        onReparent(updatedRecord);
      }
    } catch {
      showSnackbar({ text: 'Something went wrong', variant: 'danger' });
      return;
    }
  }

  return <>
    {options.map((option, i) => (
      <Dropdown.Item
        icon={option.icon || (option.iconName && iconsByName[option.iconName]) || faRightLeft}
        key={i}
        label={option.label}
        onClick={() => reparentRecord(option.parent)}
      />
    ))}
  </>;
}
